import {
  AddPrinterModalProps,
  CreateOutletPrinterDraft,
  InstalledDevicePrinter,
  PrinterConnectionType,
  PrinterPaperSize,
} from '@/renderer/types/printer';
import { AlertCircle, Printer, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, DropdownSelect, Modal } from '../../common';

const PAPER_OPTIONS: PrinterPaperSize[] = ['80mm', '58mm'];
const CONNECTION_OPTIONS: {
  value: PrinterConnectionType;
  label: string;
  description: string;
}[] = [
  {
    value: 'usb',
    label: 'USB',
    description: 'Use for installed USB receipt printers',
  },
  {
    value: 'serial',
    label: 'Serial',
    description: 'Use serial port path like /dev/usb/lp0',
  },
  {
    value: 'bluetooth',
    label: 'Bluetooth',
    description: 'Use bluetooth MAC address and RFCOMM channel',
  },
  {
    value: 'network',
    label: 'Network',
    description: 'Use IP/hostname and TCP port (usually 9100)',
  },
];

interface DraftBuildResult {
  draft: CreateOutletPrinterDraft | null;
  error: string | null;
}

const inferConnectionTypeFromInstalledPrinter = (
  printer: InstalledDevicePrinter | undefined,
): PrinterConnectionType | null => {
  if (!printer) return null;

  const optionEntries = Object.entries(printer.options || {}).map(
    ([key, value]) => `${key}:${String(value)}`,
  );
  const deviceUri = String(
    (printer.options || {})['device-uri'] ||
      (printer.options || {}).deviceUri ||
      (printer.options || {})['printer-uri-supported'] ||
      '',
  ).toLowerCase();
  const blob = [
    printer.name,
    printer.description,
    printer.displayName,
    ...optionEntries,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (!blob) return null;

  if (deviceUri.startsWith('usb://')) {
    return 'usb';
  }

  if (
    deviceUri.startsWith('serial://') ||
    deviceUri.startsWith('socket+serial://')
  ) {
    return 'serial';
  }

  if (deviceUri.startsWith('bluetooth://')) {
    return 'bluetooth';
  }

  if (
    deviceUri.startsWith('socket://') ||
    deviceUri.startsWith('ipp://') ||
    deviceUri.startsWith('lpd://') ||
    deviceUri.startsWith('http://') ||
    deviceUri.startsWith('https://')
  ) {
    return 'network';
  }

  if (
    blob.includes('bluetooth') ||
    blob.includes(' bt ') ||
    blob.includes('rfcomm') ||
    /([0-9a-f]{2}:){5}[0-9a-f]{2}/i.test(blob)
  ) {
    return 'bluetooth';
  }

  if (
    blob.includes('/dev/tty') ||
    blob.includes('/dev/usb/lp') ||
    blob.includes('serial') ||
    /\bcom\d+\b/i.test(blob)
  ) {
    return 'serial';
  }

  if (
    blob.includes('socket://') ||
    blob.includes('ipp://') ||
    blob.includes('lpd://') ||
    blob.includes('network') ||
    blob.includes('tcp')
  ) {
    return 'network';
  }

  if (blob.includes('usb')) {
    return 'usb';
  }

  return null;
};

export const AddPrinterModal: React.FC<AddPrinterModalProps> = ({
  installedPrinters,
  isOpen,
  isSaving,
  isTesting,
  onClose,
  onSubmit,
  onTest,
}) => {
  const [connectionType, setConnectionType] =
    useState<PrinterConnectionType>('usb');
  const [selectedInstalledPrinter, setSelectedInstalledPrinter] = useState('');
  const [networkHost, setNetworkHost] = useState('');
  const [networkPort, setNetworkPort] = useState('9100');
  const [serialPath, setSerialPath] = useState('');
  const [serialBaudRate, setSerialBaudRate] = useState('9600');
  const [bluetoothAddress, setBluetoothAddress] = useState('');
  const [bluetoothChannel, setBluetoothChannel] = useState('1');
  const [printerName, setPrinterName] = useState('');
  const [paperSize, setPaperSize] = useState<PrinterPaperSize>('80mm');
  const [error, setError] = useState<string | null>(null);
  const [hasTestPassed, setHasTestPassed] = useState(false);

  const selectedInstalledPrinterOption = useMemo(
    () =>
      installedPrinters.find(
        (printer) => printer.name === selectedInstalledPrinter,
      ),
    [installedPrinters, selectedInstalledPrinter],
  );

  const detectedConnectionType = useMemo(
    () =>
      inferConnectionTypeFromInstalledPrinter(selectedInstalledPrinterOption),
    [selectedInstalledPrinterOption],
  );

  const resetForm = () => {
    setConnectionType('usb');
    setSelectedInstalledPrinter('');
    setNetworkHost('');
    setNetworkPort('9100');
    setSerialPath('');
    setSerialBaudRate('9600');
    setBluetoothAddress('');
    setBluetoothChannel('1');
    setPrinterName('');
    setPaperSize('80mm');
    setError(null);
    setHasTestPassed(false);
  };

  useEffect(() => {
    if (detectedConnectionType) {
      setConnectionType(detectedConnectionType);
    }
  }, [detectedConnectionType]);

  useEffect(() => {
    setHasTestPassed(false);
  }, [
    connectionType,
    selectedInstalledPrinter,
    networkHost,
    networkPort,
    serialPath,
    serialBaudRate,
    bluetoothAddress,
    bluetoothChannel,
    printerName,
    paperSize,
  ]);

  const buildDraft = (): DraftBuildResult => {
    if (!printerName.trim()) {
      return { draft: null, error: 'Printer nick name is required' };
    }

    const resolvedConnectionType = detectedConnectionType || connectionType;
    const basePayload: CreateOutletPrinterDraft = {
      name: printerName.trim(),
      connection_type: resolvedConnectionType,
      paper_size: paperSize,
      printer_identifier: '',
      details: {},
    };

    if (selectedInstalledPrinter.trim()) {
      return {
        draft: {
          ...basePayload,
          printer_identifier: selectedInstalledPrinter.trim(),
          ip_address: null,
          port: null,
          details: {
            source: 'installed',
            driver: resolvedConnectionType,
            installedPrinterName: selectedInstalledPrinter.trim(),
            detectedConnectionType,
            description: selectedInstalledPrinterOption?.description || null,
            isDefault: selectedInstalledPrinterOption?.isDefault || false,
            options: selectedInstalledPrinterOption?.options || {},
          },
        },
        error: null,
      };
    }

    if (connectionType === 'usb') {
      return {
        draft: null,
        error:
          'Select an installed printer for USB connection type, or choose another connection type',
      };
    }

    if (connectionType === 'network') {
      const normalizedHost = networkHost.trim();
      const parsedPort = Number(networkPort);

      if (!normalizedHost) {
        return { draft: null, error: 'Network host/IP is required' };
      }

      if (
        !Number.isFinite(parsedPort) ||
        parsedPort < 1 ||
        parsedPort > 65535
      ) {
        return { draft: null, error: 'Port must be between 1 and 65535' };
      }

      return {
        draft: {
          ...basePayload,
          printer_identifier: normalizedHost,
          ip_address: normalizedHost,
          port: parsedPort,
          details: {
            source: 'manual',
            driver: 'network',
            host: normalizedHost,
            port: parsedPort,
          },
        },
        error: null,
      };
    }

    if (connectionType === 'serial') {
      const normalizedPath = serialPath.trim();
      const parsedBaudRate = Number(serialBaudRate);

      if (!normalizedPath) {
        return { draft: null, error: 'Serial path is required' };
      }

      if (!Number.isFinite(parsedBaudRate) || parsedBaudRate <= 0) {
        return {
          draft: null,
          error: 'Serial baud rate must be a positive number',
        };
      }

      return {
        draft: {
          ...basePayload,
          printer_identifier: normalizedPath,
          ip_address: null,
          port: null,
          details: {
            source: 'manual',
            driver: 'serial',
            path: normalizedPath,
            baudRate: parsedBaudRate,
          },
        },
        error: null,
      };
    }

    const normalizedAddress = bluetoothAddress.trim();
    const parsedChannel = Number(bluetoothChannel);

    if (!normalizedAddress) {
      return { draft: null, error: 'Bluetooth MAC address is required' };
    }

    if (!Number.isFinite(parsedChannel) || parsedChannel <= 0) {
      return {
        draft: null,
        error: 'Bluetooth channel must be a positive number',
      };
    }

    return {
      draft: {
        ...basePayload,
        printer_identifier: normalizedAddress,
        ip_address: null,
        port: null,
        details: {
          source: 'manual',
          driver: 'bluetooth',
          address: normalizedAddress,
          channel: parsedChannel,
        },
      },
      error: null,
    };
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleTestPrint = async () => {
    setError(null);

    const { draft, error: buildError } = buildDraft();
    if (!draft || buildError) {
      setError(buildError || 'Invalid printer configuration');
      return;
    }

    const testResult = await onTest(draft);
    setHasTestPassed(testResult);

    if (!testResult) {
      setError('Printer test failed. Review configuration and try again.');
    }
  };

  const handleSubmit = async () => {
    setError(null);

    if (!hasTestPassed) {
      setError('Print a successful test page before saving this printer');
      return;
    }

    const { draft, error: buildError } = buildDraft();
    if (!draft || buildError) {
      setError(buildError || 'Invalid printer configuration');
      return;
    }

    const saved = await onSubmit(draft);
    if (saved) {
      resetForm();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <div className="space-y-4 text-sm">
        <div className="flex items-start justify-between">
          <h3 className="text-xl font-semibold text-black">Add New Printer</h3>
          <button
            type="button"
            onClick={handleClose}
            className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-black"
          >
            <X size={22} />
          </button>
        </div>

        <div className="rounded border border-orange-200 bg-[#ead9c7] p-3 text-sm text-black">
          <div className="flex items-start gap-3">
            <Printer size={20} className="mt-0.5 text-black" />
            <div className="space-y-1">
              <p className="font-medium">
                Printer must pass test print before it can be saved.
              </p>
              <p className="text-xs">
                Select a driver type that matches the actual device transport
                (USB, Serial, Bluetooth, or Network).
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium text-black">
            Installed printers (optional)
          </p>
          <DropdownSelect
            value={selectedInstalledPrinter}
            onChange={setSelectedInstalledPrinter}
            options={[
              { value: '', label: 'No installed printer selected' },
              ...installedPrinters.map((p) => ({
                value: p.name,
                label: p.name,
              })),
            ]}
            searchable={installedPrinters.length > 5}
          />
          {detectedConnectionType ? (
            <p className="text-xs text-gray-600">
              Detected connection type from driver details:{' '}
              {detectedConnectionType}
            </p>
          ) : null}
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium text-black">Connection type</p>
          <div className="grid grid-cols-2 gap-2">
            {CONNECTION_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setConnectionType(option.value)}
                className={`rounded border px-3 py-2 text-left text-sm font-medium transition-colors ${
                  connectionType === option.value
                    ? 'border-pos-primary bg-blue-50 text-pos-primary'
                    : 'border-gray-300 bg-white text-black hover:bg-gray-50'
                }`}
              >
                <div>{option.label}</div>
                <div className="text-[11px] font-normal text-gray-500">
                  {option.description}
                </div>
              </button>
            ))}
          </div>
        </div>

        {connectionType === 'network' ? (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <p className="text-sm font-medium text-black">Host / IP</p>
              <input
                value={networkHost}
                onChange={(event) => setNetworkHost(event.target.value)}
                placeholder="e.g. 192.168.1.50"
                className="h-11 w-full rounded border border-gray-300 bg-white px-3 text-sm text-black outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
              />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-black">Port</p>
              <input
                value={networkPort}
                onChange={(event) => setNetworkPort(event.target.value)}
                placeholder="9100"
                className="h-11 w-full rounded border border-gray-300 bg-white px-3 text-sm text-black outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
              />
            </div>
          </div>
        ) : null}

        {connectionType === 'serial' ? (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <p className="text-sm font-medium text-black">Serial path</p>
              <input
                value={serialPath}
                onChange={(event) => setSerialPath(event.target.value)}
                placeholder="/dev/usb/lp0 or COM3"
                className="h-11 w-full rounded border border-gray-300 bg-white px-3 text-sm text-black outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
              />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-black">Baud rate</p>
              <input
                value={serialBaudRate}
                onChange={(event) => setSerialBaudRate(event.target.value)}
                placeholder="9600"
                className="h-11 w-full rounded border border-gray-300 bg-white px-3 text-sm text-black outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
              />
            </div>
          </div>
        ) : null}

        {connectionType === 'bluetooth' ? (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <p className="text-sm font-medium text-black">MAC address</p>
              <input
                value={bluetoothAddress}
                onChange={(event) => setBluetoothAddress(event.target.value)}
                placeholder="01:23:45:67:89:AB"
                className="h-11 w-full rounded border border-gray-300 bg-white px-3 text-sm text-black outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
              />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-black">Channel</p>
              <input
                value={bluetoothChannel}
                onChange={(event) => setBluetoothChannel(event.target.value)}
                placeholder="1"
                className="h-11 w-full rounded border border-gray-300 bg-white px-3 text-sm text-black outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
              />
            </div>
          </div>
        ) : null}

        <div className="space-y-2">
          <p className="text-sm font-medium text-black">Printer nick name</p>
          <input
            value={printerName}
            onChange={(event) => setPrinterName(event.target.value)}
            placeholder="e.g. Bar cashier"
            maxLength={40}
            className="h-11 w-full rounded border border-gray-300 bg-white px-3 text-sm text-black outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
          />
        </div>

        <DropdownSelect
          label="Paper size"
          value={paperSize}
          onChange={(v) => setPaperSize(v as PrinterPaperSize)}
          options={PAPER_OPTIONS.map((o) => ({ value: o, label: o }))}
        />

        {error ? (
          <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        ) : null}

        {hasTestPassed ? (
          <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-700">
            Test page printed successfully. You can now save this printer.
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-2 pt-1">
          <Button
            variant="outline"
            onClick={handleTestPrint}
            isLoading={isTesting}
            disabled={isSaving}
          >
            Print Test Page
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={isSaving}
            disabled={!hasTestPassed || isTesting}
          >
            Save Printer
          </Button>
        </div>
      </div>
    </Modal>
  );
};
